/**
 * The Inbox counterpart to `AiBreakdownButton`: its main lightbulb retries this one item,
 * while the familiar flyout picks the tier *this bulb* runs at. The pick belongs to the bulb
 * (`bulbTier.ts`), travels with the request, and neither changes nor follows the account's
 * capture tier in Settings (#82); the retry is deliberately the separate, primary click.
 *
 * The retry waits for the model, and what comes back is shown *in place* rather than as a
 * notice somewhere else (#79): a text area draws out of the bulb, says "Asking the AI…", and
 * then reports what happened — "Created Task: … — in Cinema", or why it didn't. The row is
 * held open while it does, even when the item was filed and so is, strictly, already gone;
 * the point of the message is to let the person see where it went before it goes.
 *
 * The bulb's own face carries the next step. A ✓ means "filed — click to clear this item
 * away" (and, unless the Display setting turns it off, it clears itself after a few seconds,
 * with a ring draining around the tick so that is never a surprise). A ✕ means "nothing was
 * filed — click to clear this message"; that one never clears itself, since a failure is the
 * thing you need time to read.
 *
 * The Create-task form's lightbulb (#82) reuses all of this: it closes the form back to the idle
 * row and mounts this with `autoStart`, `onRetry` replaced by the refine call, and
 * `leaveOnClear` — a refine resolves the old item whatever the AI said, so even an unsure or
 * failed answer is cleared by the row leaving (the new Inbox item replaces it on reload), not
 * by resetting it to be tried again.
 *
 * The sequence of layout changes lives in `inboxAiRetryMotion.ts`; this component is only
 * which phase we are in and what the bulb shows for it.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { CaptureTier, InboxAiRetryResult } from "../api";
import { useAiConfigured } from "../aiConfigured";
import { useBulbTier, type BulbSurface } from "../bulbTier";
import {
  autoClearDelay,
  createInboxAiMotion,
  prefersReducedMotion,
  type InboxAiMotion,
} from "../inboxAiRetryMotion";
import { describeRetryResult, plainText, type RetryMessage } from "../inboxAiRetryMessage";
import { pausableTimeout, type PausableTimeout } from "../pausableTimeout";
import { loadSettings } from "../settings";
import { Check, Chevron, Cross, Lightbulb, Pencil, Spinner } from "./icons";
import { SplitButton, type SplitButtonItem } from "./SplitButton";

const TIER_ORDER: readonly CaptureTier[] = ["off", "dim", "on", "bright"];

const TIER_LABEL: Record<CaptureTier, string> = {
  off: "Off",
  dim: "Dim",
  on: "On",
  bright: "Bright",
};

const TIER_BLURB: Record<CaptureTier, string> = {
  off: "AI doesn't process Inbox items",
  dim: "Fast, always available, rougher",
  on: "The default once something's configured",
  bright: "Best. Slowest or costliest, worth it when it matters",
};

type Phase = "idle" | "busy" | "done" | "resetting" | "leaving";

export function InboxAiRetryButton<T = InboxAiRetryResult>({
  surface = "inbox-retry",
  tierControl,
  onRetry,
  onSettled,
  autoStart = false,
  leaveOnClear = false,
  onEdit,
  describe = describeRetryResult as unknown as (result: T) => RetryMessage,
  label = "Retry AI",
  busyLabel = "Retrying with AI",
  doneLabel = "Clear this item",
  disabled = false,
  keepOpen = false,
  instant = false,
}: {
  /** Which bulb this is, for remembering its tier (`bulbTier.ts`). */
  surface?: BulbSurface;
  /** Takes over the tier from `surface`'s remembered pick: for a bulb that is not a free-standing
   * control but a continuation of one set elsewhere — a queue row's, which shows the tier the
   * item was added under and changes only that item's run. */
  tierControl?: { tier: CaptureTier | null; onChange: (tier: CaptureTier) => void };
  /** Runs the retry at the bulb's tier. Resolves with what the model made of it; a *failure* of
   * the model is a resolved `outcome: "failed"`, a rejection is only "couldn't ask" (network,
   * auth). */
  onRetry: (tier: CaptureTier | null) => Promise<T>;
  /** Called once the answer has been cleared away, so the card can refresh its list. `null`
   * when the request itself never got an answer. */
  onSettled: (result: T | null) => void | Promise<void>;
  /** Run `onRetry` as soon as this mounts, rather than waiting for a click. */
  autoStart?: boolean;
  /** Clear any answer by letting the row leave, not just a filed one. For a `onRetry` that
   * resolves the item whatever comes back, or a row that belongs to a section the item has just
   * left. A function decides per answer. A rejected request (nothing happened) still resets. */
  leaveOnClear?: boolean | ((result: T) => boolean);
  /** Offers an Edit button beside the ✓ when the AI made a task or note, to open it and put
   * right whatever it got wrong. Pressing it stops the answer clearing itself: the person is
   * clearly still looking at it. */
  onEdit?: (created: NonNullable<InboxAiRetryResult["created"]>) => void;
  /** What an answer says. Defaults to the Inbox's (`describeRetryResult`); an editor's bulb
   * supplies its own, since what it gets back is the edited item, not an Inbox item. */
  describe?: (result: T) => RetryMessage;
  /** The face's accessible name while idle (the tier is appended) and while it waits. */
  label?: string;
  busyLabel?: string;
  /** What the ✓ says it does once the AI has answered. "Clear this item" suits an Inbox row, which
   * goes; an editor's answer is already saved, so there it should not sound like discarding. */
  doneLabel?: string;
  /** Nothing to hand over yet. The flyout stays usable. */
  disabled?: boolean;
  /** The person has taken over the form this bulb sits in: an answer still showing stops counting
   * down and folds away, giving the buttons back, without closing anything. */
  keepOpen?: boolean;
  /** Hand the answer straight to `onSettled` instead of showing it here: for a row that redraws
   * itself as whatever the AI made (Just added). A request that could not be asked at all still
   * shows its error the usual way. */
  instant?: boolean;
}) {
  const remembered = useBulbTier(surface);
  const configured = useAiConfigured();
  const tier = tierControl ? tierControl.tier : remembered.tier;
  const setTier = tierControl ? tierControl.onChange : remembered.setTier;
  const [phase, setPhaseState] = useState<Phase>("idle");
  const [message, setMessage] = useState<RetryMessage | null>(null);
  // How long this answer's ✓ will wait before clearing itself, fixed when the answer lands
  // (so the ring and the timer always agree even if the setting changes while it is held);
  // `null` when it won't clear itself.
  const [clearsInMs, setClearsInMs] = useState<number | null>(null);
  const clusterRef = useRef<HTMLSpanElement>(null);
  const motionRef = useRef<InboxAiMotion | null>(null);
  const resultRef = useRef<T | null>(null);
  const messageRef = useRef<RetryMessage | null>(null);
  const autoClear = useRef<PausableTimeout | null>(null);
  // The answer is longer than the two lines the text area shows, and whether it is unfolded.
  const [longer, setLonger] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // The pointer is over a held answer: the countdown waits.
  const [held, setHeld] = useState(false);
  const mounted = useRef(false);
  // The auto-clear timer fires long after the render that scheduled it, so it reads the phase
  // from here rather than from a closure that still thinks it is "busy".
  const phaseRef = useRef<Phase>("idle");
  const anchorName = `--inbox-ai-retry-${useId().replace(/:/g, "")}`;

  function setPhase(next: Phase) {
    phaseRef.current = next;
    setPhaseState(next);
  }

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      autoClear.current?.cancel();
      motionRef.current?.cancel();
    };
  }, []);

  useEffect(() => {
    if (!autoStart) return;
    // Deferred a tick so a remount (StrictMode's mount, unmount, mount) starts it once: the
    // first mount's cleanup cancels the timer before it fires.
    const timer = window.setTimeout(() => void retry(), 0);
    return () => window.clearTimeout(timer);
    // `retry` is re-created every render; starting is a one-shot, keyed on `autoStart` turning
    // true — on mount for a refine, and when the worker picks a queued item up for the Queue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  async function retry() {
    const cluster = clusterRef.current;
    if (!cluster || phaseRef.current !== "idle") return;
    setPhase("busy");
    setMessage(null);
    setClearsInMs(null);
    setLonger(false);
    setExpanded(false);
    setHeld(false);
    const motion = (motionRef.current ??= createInboxAiMotion(cluster));

    // The request starts now and runs alongside the fold; if the model is quicker than the
    // animation, the answer simply waits for the text area to finish drawing out.
    const request = onRetry(tier).then<T | Error, T | Error>(
      (result) => result,
      (err: unknown) => (err instanceof Error ? err : new Error(String(err))),
    );
    let settled: T | Error;
    if (instant) {
      settled = await request;
      if (!mounted.current) return;
      if (!(settled instanceof Error)) {
        setPhase("idle");
        await onSettled(settled);
        return;
      }
      await motion.open();
    } else {
      await motion.open();
      settled = await request;
      if (!mounted.current) return;
    }

    const result = settled instanceof Error ? null : settled;
    const next: RetryMessage =
      settled instanceof Error
        ? {
            segments: [{ text: settled.message || "AI retry could not be run." }],
            tone: "bad",
            filed: false,
          }
        : describe(settled);
    resultRef.current = result;
    messageRef.current = next;
    setMessage(next);
    const overflows = await motion.answer(next.segments, next.tone);
    if (!mounted.current) return;
    setLonger(overflows);

    const delay = autoClearDelay(next.filed, loadSettings(), prefersReducedMotion());
    setClearsInMs(delay);
    setPhase("done");
    if (delay !== null) autoClear.current = pausableTimeout(() => void clear(), delay);
  }

  /** Stop the answer clearing itself, for good: the person is reading it, or has gone back to the
   * form. */
  function holdOpen() {
    autoClear.current?.cancel();
    autoClear.current = null;
    setClearsInMs(null);
  }

  function toggleExpanded() {
    const next = !expanded;
    setExpanded(next);
    motionRef.current?.expand(next);
    if (next) holdOpen(); // a long answer is being read
  }

  // The form this bulb sits in was touched while an answer showed: put the buttons back and let
  // the answer go, without clearing anything away.
  useEffect(() => {
    if (!keepOpen || phaseRef.current !== "done") return;
    holdOpen();
    void (async () => {
      setPhase("resetting");
      await motionRef.current?.reset();
      if (!mounted.current) return;
      setPhase("idle");
      setMessage(null);
      setLonger(false);
      setExpanded(false);
    })();
  }, [keepOpen]);

  async function clear() {
    const motion = motionRef.current;
    if (!motion || phaseRef.current !== "done") return;
    holdOpen();
    const result = resultRef.current;

    const leaves =
      typeof leaveOnClear === "function" ? result !== null && leaveOnClear(result) : leaveOnClear;
    if (result && (messageRef.current?.filed || leaves)) {
      // Resolved server-side already: there is nothing to put back, so the row just goes.
      setPhase("leaving");
      await motion.leave();
      if (mounted.current) await onSettled(result);
      return;
    }
    setPhase("resetting");
    await motion.reset();
    if (!mounted.current) return;
    setPhase("idle");
    setMessage(null);
    await onSettled(result);
  }

  const items: SplitButtonItem[] = TIER_ORDER.map((candidate) => ({
    key: candidate,
    label: (
      <span className="ai-breakdown-menu-item">
        <Lightbulb level={candidate} size={14} />
        {TIER_LABEL[candidate]} - {TIER_BLURB[candidate]}
      </span>
    ),
    onSelect: () => setTier(candidate),
  }));

  const holding = phase === "done" || phase === "leaving";
  const face = holding ? (
    message?.filed ? (
      <>
        <Check size={14} />
        {phase === "done" && clearsInMs !== null && <Ring ms={clearsInMs} paused={held} />}
      </>
    ) : (
      <Cross size={14} />
    )
  ) : phase === "busy" ? (
    <Spinner size={14} />
  ) : (
    <Lightbulb level={tier ?? "off"} size={14} />
  );

  const mainAriaLabel =
    phase === "busy"
      ? busyLabel
      : holding
        ? message?.filed
          ? doneLabel
          : "Clear message"
        : `${label}${tier ? ` (${TIER_LABEL[tier]})` : ""}`;

  // The Inbox's own answer: what the AI made, when it made something, for the Edit button.
  const made = (resultRef.current as InboxAiRetryResult | null)?.created ?? null;

  // Not drawn on a server with no AI to ask (`aiConfigured.ts`); never taken away mid-attempt.
  if (!configured && phase === "idle") return null;

  return (
    <span
      className="inbox-ai-retry"
      ref={clusterRef}
      // The countdown waits while the pointer is over a held answer. Only the pointer arriving
      // pauses it: one already resting on the bulb where it was pressed does not.
      onPointerEnter={() => {
        if (phaseRef.current !== "done" || !autoClear.current) return;
        autoClear.current.pause();
        setHeld(true);
      }}
      onPointerLeave={() => {
        if (!autoClear.current) return;
        autoClear.current.resume();
        setHeld(false);
      }}
    >
      <span className="inbox-ai-slot">
        {/* Tapping anywhere in a held answer stops it clearing itself, and unfolds or folds a long
            one: the chevron alone is a small target. Pointer only; the chevron is the keyboard's. */}
        <span
          className={`inbox-ai-box${phase === "done" ? " is-held-able" : ""}`}
          onClick={() => {
            if (phaseRef.current !== "done") return;
            holdOpen();
            if (longer) toggleExpanded();
          }}
        >
          {/* The visible text is decoration — it types itself out, which a screen reader would
              announce a character at a time. The answer is announced once, whole, below. */}
          <span className="inbox-ai-text" aria-hidden="true">
            <span className="inbox-ai-sizer" />
            <span className="inbox-ai-typed" />
          </span>
          {longer && phase === "done" && (
            <button
              type="button"
              className="inbox-ai-more"
              aria-expanded={expanded}
              aria-label={expanded ? "Show less of the answer" : "Show the whole answer"}
              onClick={(event) => {
                event.stopPropagation(); // the box's own tap would toggle it straight back
                holdOpen();
                toggleExpanded();
              }}
            >
              <Chevron size={14} />
            </button>
          )}
        </span>
      </span>
      <span className="capture-tier-picker">
        <SplitButton
          label={<span className="inbox-ai-face">{face}</span>}
          mainAriaLabel={mainAriaLabel}
          toggleAriaLabel="Change the AI tier"
          disabled={phase === "busy"}
          mainDisabled={phase === "idle" && (tier === "off" || disabled)}
          onClick={() => {
            if (phase === "idle") void retry();
            else if (phase === "done") void clear();
          }}
          items={items}
          anchorName={anchorName}
        />
      </span>
      {onEdit && phase === "done" && messageRef.current?.filed && made && (
        <button
          type="button"
          className="btn ghost small inbox-ai-edit"
          aria-label={`Edit the ${made.kind === "context" ? "Context entry" : made.kind} the AI made`}
          title={`Edit the ${made.kind === "context" ? "Context entry" : made.kind} the AI made`}
          onClick={() => {
            holdOpen();
            onEdit(made);
          }}
        >
          <Pencil />
        </button>
      )}
      <span className="sr-only" role={message?.tone === "bad" ? "alert" : "status"}>
        {message ? plainText(message.segments) : ""}
      </span>
    </span>
  );
}

/** The countdown around the ✓: a faint full ring with an arc draining out of it (held while the
 * pointer is over the answer, with the timer). */
export function Ring({ ms, paused }: { ms: number; paused: boolean }) {
  return (
    <svg className="inbox-ai-ring" viewBox="0 0 36 36" aria-hidden="true" focusable="false">
      <circle className="inbox-ai-ring-track" cx="18" cy="18" r="15.9155" />
      <circle
        className="inbox-ai-ring-arc"
        cx="18"
        cy="18"
        r="15.9155"
        pathLength={100}
        style={{ animationDuration: `${ms}ms`, animationPlayState: paused ? "paused" : "running" }}
      />
    </svg>
  );
}
